import { createDownloadBlobByFileName } from '@utils/downloadBlob'
import { useReducer, useState, useEffect, useCallback } from 'react'
import api, { IAssetTag, Order, IPageInfo } from '@api'
import { uniqueId, debounce } from 'lodash'
import { ApolloError } from 'apollo-boost'
import { Toaster } from '@ui'
import track from '@track'

const PAGE_SIZE = 25
const MAX_PAGE_SIZE = 150

type SortName = keyof Omit<IAssetTag, 'id'>

interface QueryVars {
  after: string | null
  orderBy: string
  first: number
  queryString?: string
}

interface IAssetsHookState {
  reqId: string
  items: IAssetTag[]
  sort: { column: SortName; order: Order }
  after: string | null
  query: string
  first: number
  totalAssets: number
  pageInfo: IPageInfo
  isSorting: boolean
  isLoading: boolean
  isPaging: boolean
}

export interface IAssetsHook extends IAssetsHookState {
  reload: () => void
  sortBy: (column: string) => void
  nextPage: () => void
  remove: (id: string) => Promise<never>
  search: (name: string) => void
  fetchTemplate: () => void
  fetchExportLink: () => Promise<string>
  fetchSpacesTemplate: () => void
}

enum Actions {
  LOAD_START,
  LOAD_START_WITHOUT_SKELETON,
  LOAD,
  SORT,
  PAGE,
  NEXT_PAGE,
  SEARCH,
}

interface LoadStartAction {
  type: Actions.LOAD_START
  reqId: string
}
interface LoadStartActionWithoutSkeleton {
  type: Actions.LOAD_START_WITHOUT_SKELETON
  reqId: string
}

interface LoadAction {
  type: Actions.LOAD
  reqId: string
  payload: { items: IAssetTag[]; pageInfo: IPageInfo; totalAssets: number }
}

interface SortAction {
  type: Actions.SORT
  payload: {
    column: SortName
  }
}

interface SearchAction {
  type: Actions.SEARCH
  payload: { name: string }
}

interface NextPageAction {
  type: Actions.NEXT_PAGE
}

type Action =
  | LoadAction
  | SortAction
  | NextPageAction
  | LoadStartAction
  | LoadStartActionWithoutSkeleton
  | SearchAction

function reducer(state: IAssetsHookState, action: Action) {
  switch (action.type) {
    case Actions.LOAD_START:
      return { ...state, isLoading: true, reqId: action.reqId }
    case Actions.LOAD_START_WITHOUT_SKELETON:
      return {
        ...state,
        isLoading: false,
        reqId: action.reqId,
      }
    case Actions.LOAD:
      return action.reqId === state.reqId
        ? {
            ...state,
            isLoading: false,
            isSorting: false,
            isPaging: false,
            items: state.isPaging
              ? [...state.items, ...action.payload.items]
              : action.payload.items,
            pageInfo: action.payload.pageInfo,
            totalAssets: action.payload.totalAssets,
          }
        : state
    case Actions.NEXT_PAGE:
      return {
        ...state,
        after: state.pageInfo.endCursor,
        isPaging: true,
        first: Math.min(MAX_PAGE_SIZE, state.first + PAGE_SIZE),
      }

    case Actions.SORT:
      return {
        ...state,
        sort: {
          order:
            action.payload.column === state.sort.column
              ? state.sort.order === Order.DESC
                ? Order.ASC
                : Order.DESC
              : Order.DESC,
          column: action.payload.column,
        },
        isSorting: true,
      }

    case Actions.SEARCH:
      return {
        ...state,
        query: action.payload.name,
        after: null,
      }

    default:
      return state
  }
}

let persistedState: IAssetsHookState = {
  reqId: uniqueId('request'),
  items: [],
  sort: { column: 'name', order: Order.ASC },
  pageInfo: { hasNextPage: false, endCursor: '' },
  after: null,
  first: PAGE_SIZE,
  query: '',
  totalAssets: 0,
  isSorting: false,
  isLoading: true,
  isPaging: false,
}

const downloadBlob = createDownloadBlobByFileName('assetsImports_template.csv')

export default function useAssets(): IAssetsHook {
  const [state, dispatch] = useReducer(reducer, persistedState)
  const [shouldReload, setShouldReload] = useState(false)

  const debouncedFetch = useCallback(
    debounce((query: string, reqId: string, vars: QueryVars) => {
      api.asset
        .list(vars)
        .then(({ assetTags, pageInfo, counters }) => {
          dispatch({
            type: Actions.LOAD,
            payload: {
              items: assetTags,
              pageInfo,
              totalAssets: counters.total,
            },
            reqId,
          })
        })
        .catch((error: ApolloError) => Toaster.fail({ error }))
    }, 300),
    []
  )

  const fetchAssetTags =
    ({
      loadAll,
      showSkeleton,
      nullAfter,
    }: {
      loadAll: boolean
      showSkeleton: boolean
      nullAfter: boolean
    }) =>
    () => {
      const reqId = uniqueId('request')
      const variables: QueryVars = {
        after: loadAll || nullAfter ? null : state.after,
        orderBy: `${state.sort.column}_${state.sort.order}`,
        first: loadAll ? state.first : PAGE_SIZE,
        queryString: state.query,
      }

      if (showSkeleton) {
        dispatch({ type: Actions.LOAD_START, reqId })
      } else {
        dispatch({ type: Actions.LOAD_START_WITHOUT_SKELETON, reqId })
      }

      setShouldReload(false)

      debouncedFetch(state.query, reqId, variables)

      return () => debouncedFetch.cancel()
    }

  const fetchTemplate = () => {
    api.asset.template().then(downloadBlob)
  }

  const fetchSpacesTemplate = () => {
    api.asset.spacesTemplate().then(downloadBlob)
  }

  useEffect(fetchAssetTags({ showSkeleton: false, loadAll: true, nullAfter: true }), [
    state.sort,
    shouldReload,
  ])

  //loading data without skeleton and load only +25 items
  useEffect(fetchAssetTags({ showSkeleton: false, loadAll: false, nullAfter: false }), [
    state.after,
  ])

  useEffect(fetchAssetTags({ showSkeleton: true, loadAll: false, nullAfter: true }), [state.query])

  useEffect(() => {
    persistedState = { ...state, after: null }
  })

  const fetchExportLink = () => {
    return api.asset.csv({ orderBy: `${state.sort.column}_${state.sort.order}`, where: null })
  }

  return {
    ...state,
    fetchTemplate,
    fetchExportLink,
    fetchSpacesTemplate,
    reload: () => setShouldReload(true),
    sortBy: (column) => {
      dispatch({ type: Actions.SORT, payload: { column: column as SortName } })
      track.assets({
        eventName: track.Event.SORT,
        props: { columnName: column },
      })
    },
    nextPage: () => {
      dispatch({ type: Actions.NEXT_PAGE })
      track.assets({
        eventName: track.Event.PAGE,
      })
    },
    remove: (id) => api.asset.remove(id),
    search: (name: string) => dispatch({ type: Actions.SEARCH, payload: { name } }),
  }
}
